import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Pagination
} from '@mui/material';
import { Casino as GachaIcon, Close as CloseIcon } from '@mui/icons-material';
import AvatarCard from './AvatarCard';
import GachaAnimation from './GachaAnimation';
import axios from '../../api/axios';

function GachaPoolDialog({ open, onClose, collection, avatars, userPoints, gachaInfo, isTeacher, onGachaSuccess }) {
  const [gachaLoading, setGachaLoading] = useState(false);
  const [gachaResult, setGachaResult] = useState(null);
  const [page, setPage] = useState(1);
  const [showAnimation, setShowAnimation] = useState(false);
  
  // Items per page: 2 rows, calculate based on grid (assuming 6 items per row for md breakpoint)
  const itemsPerRow = 6;
  const rowsPerPage = 2;
  const itemsPerPage = itemsPerRow * rowsPerPage;

  // Filter and sort avatars by collection and rarity
  const sortedAvatars = useMemo(() => {
    let poolAvatars = collection 
      ? avatars.filter(a => a.collection === collection)
      : avatars;
    
    // Sort by rarity (SSR > SR > R), then by name
    const rarityOrder = { SSR: 0, SR: 1, R: 2 };
    return poolAvatars.sort((a, b) => {
      const rarityDiff = rarityOrder[a.rarity] - rarityOrder[b.rarity];
      if (rarityDiff !== 0) return rarityDiff;
      return a.name.localeCompare(b.name);
    });
  }, [avatars, collection]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedAvatars.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageAvatars = sortedAvatars.slice(startIndex, endIndex);

  // Reset to page 1 when collection changes
  React.useEffect(() => {
    setPage(1);
  }, [collection]);

  const handleGacha = async () => {
    if (gachaLoading) return;
    
    if (!isTeacher && userPoints.points < (gachaInfo?.pullCost || 100)) {
      alert('积分不足！完成题目可以获得积分。');
      return;
    }

    try {
      setGachaLoading(true);
      setGachaResult(null);
      const response = await axios.post('/avatars/gacha', { collection });
      const result = response.data;
      
      // Show animation
      setShowAnimation(true);
      setGachaResult(result);
      
      // Don't refresh data yet - wait for animation to complete
    } catch (err) {
      console.error('Error performing gacha:', err);
      alert('抽卡失败：' + (err.response?.data?.error || err.message));
      setGachaLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getCollectionName = () => {
    if (!collection) return '全部卡池';
    if (collection === 'cat') return '🐱 猫咪卡池';
    if (collection === 'human') return '👤 人类卡池';
    return collection;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{getCollectionName()}</Typography>
          <Button onClick={onClose} size="small">
            <CloseIcon />
          </Button>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            {currentPageAvatars.map((avatar) => (
              <Grid item xs={4} sm={3} md={2} key={avatar.id}>
                <AvatarCard 
                  avatar={avatar} 
                  owned={avatar.owned}
                  selected={false}
                  hideName={true}
                />
              </Grid>
            ))}
          </Grid>
          
          {sortedAvatars.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                该卡池暂无头像
              </Typography>
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>

      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          {!isTeacher && (
            <Typography variant="body2" color="text.secondary">
              积分: {userPoints?.points || 0} / 消耗: {gachaInfo?.pullCost || 100}
            </Typography>
          )}
          {gachaInfo && (
            <Typography variant="body2" color="text.secondary">
              保底: {gachaInfo.pityCount} / {gachaInfo.nextGuaranteedSR > 0 ? gachaInfo.nextGuaranteedSR : gachaInfo.nextGuaranteedSSR}
            </Typography>
          )}
        </Stack>
        <Button onClick={onClose}>
          关闭
        </Button>
        <Button
          variant="contained"
          startIcon={gachaLoading ? <CircularProgress size={20} /> : <GachaIcon />}
          onClick={handleGacha}
          disabled={gachaLoading || (!isTeacher && (userPoints?.points || 0) < (gachaInfo?.pullCost || 100))}
        >
          {gachaLoading ? '抽卡中...' : isTeacher ? '免费抽卡' : `抽卡 (${gachaInfo?.pullCost || 100} 积分)`}
        </Button>
      </DialogActions>
      
      <GachaAnimation
        open={showAnimation}
        onClose={async () => {
          setShowAnimation(false);
          setGachaLoading(false);
          
          // Refresh data when user closes
          if (onGachaSuccess) {
            await onGachaSuccess();
          }
          
          // Clear result after closing
          setTimeout(() => setGachaResult(null), 500);
        }}
        result={gachaResult}
        avatars={sortedAvatars}
        onAnimationComplete={async () => {
          // Refresh data after animation completes (but don't close)
          if (onGachaSuccess) {
            await onGachaSuccess();
          }
        }}
      />
    </Dialog>
  );
}

export default GachaPoolDialog;
